import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useStripe, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import type { Stripe, PaymentRequest, PaymentRequestPaymentMethodEvent } from '@stripe/stripe-js';
import { CreditCard, Wallet, Banknote, Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { featureFlags } from '@/config/featureFlags';

type PaymentMethod = 'card' | 'paypal' | 'venmo';

interface PaymentOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tierName: string;
  price: number;
  onPayWithCard: () => void;
}

const PaymentOptionsModal: React.FC<PaymentOptionsModalProps> = ({ isOpen, onClose, tierName, price, onPayWithCard }) => {
  const stripe = useStripe() as Stripe | null;
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [canMakePayment, setCanMakePayment] = useState<boolean | null>(null);
  const [loadingMethod, setLoadingMethod] = useState<PaymentMethod | null>(null);

  // Force close if payments are disabled
  useEffect(() => {
    if (!featureFlags.enablePayments && isOpen) {
      onClose();
      toast({
        title: "Payments Unavailable",
        description: "Payment processing is currently disabled.",
        variant: "destructive"
      });
    }
  }, [isOpen, onClose]);

  useEffect(() => {
    if (stripe && isOpen && featureFlags.enablePayments) {
      const pr = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: `${tierName} Sponsorship`,
          amount: price * 100,
        },
        requestPayerName: true,
        requestPayerEmail: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
          setCanMakePayment(true);
        } else {
          setCanMakePayment(false);
        }
      });

      pr.on('paymentmethod', async (ev: PaymentRequestPaymentMethodEvent) => {
         ev.complete('success');
         toast({
            title: "Sponsorship Confirmed!",
            description: `Thank you for your generous ${tierName} sponsorship via Digital Wallet!`,
            duration: 5000,
            className: "bg-green-50 border-green-200",
          });
          onClose();
      });
    }
  }, [stripe, price, tierName, isOpen, onClose]);

  const handleExternalPayment = (method: 'paypal' | 'venmo'): void => {
    if (!featureFlags.enablePayments) return;

    setLoadingMethod(method);

    // Simulate API call/redirect
    setTimeout(() => {
      setLoadingMethod(null);
      if (method === 'paypal') {
        window.open('https://www.paypal.com/checkoutnow', '_blank');
        toast({
          title: "Redirecting to PayPal",
          description: "Please complete your transaction in the new window.",
        });
      } else if (method === 'venmo') {
        window.open('https://venmo.com', '_blank');
        toast({
            title: "Opening Venmo",
            description: "Please complete your transaction via Venmo app or web.",
        });
      }
      onClose();
    }, 1500);
  };

  const handleCardClick = (): void => {
    if (!featureFlags.enablePayments) return;

    setLoadingMethod('card');
    setTimeout(() => {
        setLoadingMethod(null);
        onPayWithCard();
    }, 500);
  };

  if (!featureFlags.enablePayments) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px] bg-[#FAF4ED] p-0 max-h-[90vh] overflow-y-auto shadow-2xl border-0 rounded-xl">
        <div className="bg-[#3C5999] p-6 text-center">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif text-[#FAF4ED]">Select Payment Method</DialogTitle>
            <DialogDescription className="text-[#FAF4ED]/80 text-lg mt-2">
              Complete your {tierName} sponsorship
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 text-3xl font-bold text-[#D4AF6A]">
            ${price.toLocaleString()}
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Digital Wallet (Apple Pay / Google Pay) */}
          {paymentRequest && canMakePayment && (
            <div className="w-full mb-2">
               <div className="h-12 w-full">
                <PaymentRequestButtonElement
                    options={{
                        paymentRequest,
                        style: {
                            paymentRequestButton: {
                                theme: 'dark',
                                height: '48px'
                            }
                        }
                    }}
                />
               </div>
               <p className="text-xs text-center text-gray-500 mt-2 flex items-center justify-center gap-1">
                 <Wallet className="w-3 h-3" />
                 Fast, secure checkout with your device
               </p>
            </div>
          )}

          {/* Credit Card */}
          <Button
            variant="outline"
            className="w-full h-16 flex items-center justify-between px-6 border-2 border-[#3C5999]/10 hover:border-[#3C5999] hover:bg-white bg-white group transition-all duration-300"
            onClick={handleCardClick}
            disabled={loadingMethod === 'card'}
          >
            <div className="flex items-center gap-4">
              <div className="bg-[#3C5999]/10 p-2 rounded-full group-hover:bg-[#3C5999] transition-colors duration-300">
                <CreditCard className="w-6 h-6 text-[#3C5999] group-hover:text-white" />
              </div>
              <span className="text-lg font-bold text-[#3C5999]">Credit / Debit Card</span>
            </div>
            {loadingMethod === 'card' ? <Loader2 className="w-5 h-5 animate-spin text-[#D4AF6A]" /> : <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#D4AF6A]" />}
          </Button>

          {/* PayPal */}
          <Button
            variant="outline"
            className="w-full h-16 flex items-center justify-between px-6 border-2 border-[#3C5999]/10 hover:border-[#003087] hover:bg-white bg-white group transition-all duration-300"
            onClick={() => handleExternalPayment('paypal')}
            disabled={loadingMethod === 'paypal'}
          >
            <div className="flex items-center gap-4">
              <div className="bg-[#003087]/10 p-2 rounded-full group-hover:bg-[#003087] transition-colors duration-300">
                <span className="font-bold text-[#003087] group-hover:text-white italic text-lg px-1">P</span>
              </div>
              <span className="text-lg font-bold text-[#003087]">PayPal</span>
            </div>
            {loadingMethod === 'paypal' ? <Loader2 className="w-5 h-5 animate-spin text-[#003087]" /> : <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#003087]" />}
          </Button>

          {/* Venmo / Cash App */}
          <Button
            variant="outline"
            className="w-full h-16 flex items-center justify-between px-6 border-2 border-[#3C5999]/10 hover:border-[#008CFF] hover:bg-white bg-white group transition-all duration-300"
            onClick={() => handleExternalPayment('venmo')}
            disabled={loadingMethod === 'venmo'}
          >
             <div className="flex items-center gap-4">
              <div className="bg-[#008CFF]/10 p-2 rounded-full group-hover:bg-[#008CFF] transition-colors duration-300">
                <Banknote className="w-6 h-6 text-[#008CFF] group-hover:text-white" />
              </div>
              <span className="text-lg font-bold text-[#008CFF]">Venmo</span>
            </div>
            {loadingMethod === 'venmo' ? <Loader2 className="w-5 h-5 animate-spin text-[#008CFF]" /> : <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#008CFF]" />}
          </Button>

          <div className="pt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
             <ShieldCheck className="w-4 h-4 text-[#3C5999]" />
             <span>All transactions are secure and encrypted.</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PaymentOptionsModal;
